/**
 * Parses OAuth2 authorization callback parameters from the popup window URL.
 * Supports fragment (code) and query (some error responses) per RFC 6749.
 */
export type OAuth2CallbackParseResult =
    | {
          kind: 'authcode';
          code: string;
          state?: string;
      }
    | { kind: 'error'; error: string; errorDescription?: string; state?: string }
    | { kind: 'empty' };

const pickFirst = (params: URLSearchParams, key: string): string | undefined => {
    const value = params.get(key);
    return value === null || value === '' ? undefined : value;
};

const parseParams = (params: URLSearchParams): OAuth2CallbackParseResult => {
    const err = pickFirst(params, 'error');
    if (err) {
        return {
            kind: 'error',
            error: err,
            errorDescription: pickFirst(params, 'error_description'),
            state: pickFirst(params, 'state'),
        };
    }

    const code = pickFirst(params, 'code');
    if (code) {
        return {
            kind: 'authcode',
            code,
            state: pickFirst(params, 'state'),
        };
    }

    return { kind: 'empty' };
};

export const parseOAuth2CallbackUrl = (
    url: string | URL = typeof window !== 'undefined' ? window.location.href : ''
): OAuth2CallbackParseResult => {
    const href = typeof url === 'string' ? url : url.href;
    const asUrl = new URL(href, 'https://placeholder.local');

    const hash = asUrl.hash.startsWith('#') ? asUrl.hash.slice(1) : asUrl.hash;
    if (hash) {
        const fromHash = parseParams(new URLSearchParams(hash));
        if (fromHash.kind !== 'empty') {
            return fromHash;
        }
    }

    return parseParams(asUrl.searchParams);
};
